import { useTranslation } from 'react-i18next';
import { parseDateInput } from '../../utils/formatters';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = parseDateInput(value);
  if (!date) return '-';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd} ${hh}:${min}`;
};

export function PointsLedgerModal({
  open,
  onClose,
  items,
  loading,
  error,
  filter,
  onFilterChange,
  onLoadMore,
  hasMore,
  loadingMore,
}) {
  const { t } = useTranslation();
  if (!open) return null;

  const getEventLabel = (item) => {
    const type = item?.event_type;
    if (type === 'purchase' && item?.metadata?.resource_type === 'market') {
      return t('points.event.unlockMatch', '解锁比赛');
    }
    return t(`points.event.${type}`, type || '-');
  };

  const buildSubline = (item) => {
    const parts = [];
    const refId = item?.metadata?.resource_id || item?.reference_id;
    if (refId) {
      parts.push(`${t('points.ref', '关联')}: ${refId}`);
    }
    if (item?.direction === 'credit' && item?.expires_at) {
      parts.push(`${t('points.expiresAt', '有效期')}: ${formatDateTime(item.expires_at)}`);
    }
    return parts.join(' · ');
  };

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-lg rounded-2xl border border-glass-border bg-surface/95 p-5 text-slate-100 shadow-[0_30px_70px_rgba(0,0,0,0.45)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-slate-500">
              {t('points.balance', '点数')}
            </div>
            <div className="mt-2 text-lg font-semibold text-white">
              {t('points.ledgerTitle', '点数明细')}
            </div>
          </div>
          <button
            className="rounded-full border border-glass-border p-2 text-slate-300 hover:text-white"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="mt-4">
          <div className="flex items-center gap-2 mb-4">
            {[
              { id: 'all', label: t('points.filter.all', '全部') },
              { id: 'credit', label: t('points.filter.income', '收入') },
              { id: 'debit', label: t('points.filter.expense', '支出') },
            ].map((option) => {
              const active = filter === option.id;
              return (
                <button
                  key={option.id}
                  onClick={() => onFilterChange?.(option.id)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                    active
                      ? 'bg-accent/20 text-accent border border-accent/40'
                      : 'bg-surface/70 text-slate-400 border border-glass-border hover:text-slate-200'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="text-center text-sm text-gray-400 py-8">
              {t('points.ledgerLoading', '加载中...')}
            </div>
          ) : error ? (
            <div className="text-center text-sm text-negative py-8">
              {error}
            </div>
          ) : items?.length ? (
            <div className="space-y-3 max-h-[420px] overflow-auto scrollbar-thin pr-1">
              {items.map((item) => {
                const isCredit = item?.direction === 'credit';
                const amountText = `${isCredit ? '+' : '-'}${item?.amount ?? 0}`;
                const createdAt = formatDateTime(item?.created_at);
                const subtitle = buildSubline(item);
                return (
                  <div
                    key={item.id}
                    className="flex items-center justify-between rounded-xl border border-glass-border bg-surface/70 px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold ${isCredit ? 'bg-positive/15 text-positive' : 'bg-negative/15 text-negative'}`}>
                        {isCredit ? '+' : '-'}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-100">
                          {getEventLabel(item)}
                        </div>
                        <div className="text-xs text-slate-400">
                          {subtitle || createdAt}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${isCredit ? 'text-positive' : 'text-negative'}`}>
                        {amountText}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {createdAt}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center text-sm text-gray-400 py-8">
              {t('points.ledgerEmpty', '暂无点数记录')}
            </div>
          )}
        </div>

        {hasMore && !loading && !error && (
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="mt-4 w-full rounded-xl border border-glass-border px-4 py-2 text-sm font-semibold text-slate-200 hover:border-white/20 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loadingMore ? t('points.ledgerLoading', '加载中...') : t('points.loadMore', '加载更多')}
          </button>
        )}
      </div>
    </div>
  );
}
